export const computeRequestStats = (requests) => ({
  total: requests.length,
  SUBMITTED: requests.filter((r) => r.status === "SUBMITTED").length,
  NO_CHANGES_REQUIRED: requests.filter(
    (r) => r.status === "NO_CHANGES_REQUIRED"
  ).length,
  IN_PROGRESS: requests.filter((r) => r.status === "IN_PROGRESS").length,
  ON_HOLD: requests.filter((r) => r.status === "ON_HOLD").length,
  CHANGES_REQUESTED: requests.filter((r) => r.status === "CHANGES_REQUESTED")
    .length,
  INFO_NEEDED: requests.filter((r) => r.status === "INFO_NEEDED").length,
  IN_REVIEW: requests.filter((r) => r.status === "IN_REVIEW").length,
  COMPLETED: requests.filter((r) => r.status === "COMPLETED").length,
  CANCELLED: requests.filter((r) => r.status === "CANCELLED").length,
});

// Only EDIT_TEMPLATE requests are resumable today. Continuing a NEW_TEMPLATE
// request has no clear semantic (the new template doesn't exist yet), and
// terminal statuses (completed, cancelled, marked no-changes-required) can't
// be progressed further. Resuming also needs the original file to download.
//
// Without backend update support, reopening an in-flight EDIT from the card's
// "Edit Annotations & Field Placements" button creates a new request alongside
// the original — accepted limitation until
// update_template_request supports placements and annotations too.
export const RESUMABLE_REQUEST_TYPES = new Set(["EDIT_TEMPLATE"]);
export const TERMINAL_STATUSES = new Set([
  "COMPLETED",
  "CANCELLED",
  "NO_CHANGES_REQUIRED",
]);

export const DESCRIPTION_PROMPT_STATUSES = new Set([
  "SUBMITTED",
  "ON_HOLD",
  "CHANGES_REQUESTED",
  "INFO_NEEDED",
]);

export const canResumeRequest = (request, onContinueEdit) =>
  RESUMABLE_REQUEST_TYPES.has(request?.type) &&
  !TERMINAL_STATUSES.has(request?.status) &&
  !!request?.s3FileId &&
  typeof onContinueEdit === "function";

// The backend appends each status message to the single `description` string
// as an audit line followed by the message itself, so the only way to recover
// the parts is to find the audit lines and slice between them. Nothing here
// may drop text: anything outside an audit line belongs to some message, or
// to the original request.
const AUDIT_LINE = /^Added by (\S+) at (\S+) when setting to state (\S+)$/gm;

export const parseDescription = (description) => {
  if (typeof description !== "string" || !description.trim()) {
    return { original: "", entries: [] };
  }

  const matches = [...description.matchAll(AUDIT_LINE)];
  if (matches.length === 0) {
    return { original: description.trim(), entries: [] };
  }

  return {
    original: description.slice(0, matches[0].index).trim(),
    entries: matches.map((match, index) => {
      const next = matches[index + 1];
      return {
        author: match[1],
        timestamp: match[2],
        status: match[3],
        message: description
          .slice(
            match.index + match[0].length,
            next ? next.index : description.length
          )
          .trim(),
      };
    }),
  };
};

// `fileType` arrives either as a MIME type or as a bare word, and a template's
// configured name often carries no extension at all, which is what leaves a
// download unopenable.
//
// Two to four trailing characters, so a version number does not read as an
// extension: `Schedule v1.2` still gains `.docx`, `Schedule.docx` does not.
const HAS_EXTENSION = /\.[a-z0-9]{2,4}$/i;

const extensionFor = (fileType) => {
  const type = typeof fileType === "string" ? fileType.toLowerCase() : "";
  if (type === "application/pdf" || type === "pdf") {
    return "pdf";
  }
  return "docx";
};

export const downloadFileName = (request) => {
  const name =
    typeof request?.templateName === "string"
      ? request.templateName.trim()
      : "";
  if (HAS_EXTENSION.test(name)) {
    return name;
  }
  return `${name || "template"}.${extensionFor(request?.fileType)}`;
};
